import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { KeyRound, Mail, CheckCircle, AlertCircle } from "lucide-react";
import { useAuth } from "../auth/AuthContext";

export const ForgotPasswordPage = () => {
  const { requestPasswordReset } = useAuth();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await requestPasswordReset(email);
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-24 min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white p-8 md:p-10 shadow-lg border border-slate-200">
          <div className="text-center mb-8">
            <div className="w-14 h-14 bg-[#0B2343] mx-auto mb-4 flex items-center justify-center">
              <KeyRound className="text-[#C89B3C]" size={26} />
            </div>
            <h1 className="font-serif text-3xl font-bold text-slate-900">Reset your password</h1>
            <p className="text-slate-500 text-sm mt-2">
              Enter your email and we'll send you a reset link
            </p>
          </div>

          {submitted ? (
            <div className="text-center py-6" data-testid="forgot-success">
              <CheckCircle className="text-green-600 mx-auto mb-3" size={44} />
              <p className="text-slate-700">
                If an account exists for that email, a reset link has been sent. Please check your inbox.
              </p>
              <Link
                to="/login"
                className="inline-block mt-6 text-[#C89B3C] font-medium hover:underline"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-form">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 focus:border-slate-900 outline-none"
                    data-testid="forgot-email"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#0B2343] text-white py-3 font-medium hover:bg-slate-800 transition-colors disabled:opacity-50"
                data-testid="forgot-submit"
              >
                {submitting ? "Sending…" : "Send reset link"}
              </button>
              <div className="text-center text-sm">
                <Link to="/login" className="text-slate-600 hover:text-slate-900">
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { confirmPasswordReset } = useAuth();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setSubmitting(true);
    try {
      await confirmPasswordReset(token, password);
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Reset failed. The link may have expired.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-24 min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white p-10 shadow-lg border border-slate-200">
        <div className="text-center mb-6">
          <h1 className="font-serif text-3xl font-bold text-slate-900">Choose a new password</h1>
        </div>
        {done ? (
          <div className="text-center py-6">
            <CheckCircle className="text-green-600 mx-auto mb-3" size={44} />
            <p className="text-slate-700">Password reset. Redirecting to sign in…</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 flex items-start gap-2 text-sm">
                <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <input
              type="password"
              required
              placeholder="New password (min 8 characters)"
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:border-slate-900 outline-none"
              data-testid="reset-password"
            />
            <input
              type="password"
              required
              placeholder="Confirm password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:border-slate-900 outline-none"
              data-testid="reset-confirm"
            />
            <button
              type="submit"
              disabled={submitting || !token}
              className="w-full bg-[#0B2343] text-white py-3 font-medium hover:bg-slate-800 transition-colors disabled:opacity-50"
              data-testid="reset-submit"
            >
              {submitting ? "Resetting…" : "Reset password"}
            </button>
            {!token && (
              <p className="text-sm text-red-600 text-center">
                Missing reset token. Please use the link from your email.
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  );
};
